import type { RenderDate } from "@jetblue/kit/calendar";
import { useCallback } from "react";
import {
  useBestFaresCalendar,
  type UseBestFaresCalendarInput,
} from "../../hooks/use-best-fares-calendar";
import { BestFaresDateCell } from "./date-cell";

type FareDirection = "inbound" | "outbound";
type UseBestFaresRenderDateProperties = Pick<
  UseBestFaresCalendarInput,
  | "destination"
  | "endDate"
  | "origin"
  | "passengers"
  | "startDate"
  | "tripType"
  | "usePoints"
> & {
  fareDirection: FareDirection;
  showDatePickerPricing: boolean;
};

export const useBestFaresRenderDate = ({
  fareDirection,
  ...bestFaresCalendarInput
}: UseBestFaresRenderDateProperties) => {
  const { usePoints } = bestFaresCalendarInput;
  const {
    isLoading,
    monthlyMins: {
      inbound: inboundMonthlyMinsByDate,
      outbound: outboundMonthlyMinsByDate,
    },
    prices: {
      currencyCode,
      inbound: inboundFaresByDate,
      outbound: outboundFaresByDate,
    },
  } = useBestFaresCalendar(bestFaresCalendarInput);

  const faresByDate =
    fareDirection === "inbound" ? inboundFaresByDate : outboundFaresByDate;
  const monthlyMinsByDate =
    fareDirection === "inbound"
      ? inboundMonthlyMinsByDate
      : outboundMonthlyMinsByDate;

  return useCallback<RenderDate>(
    ({ date, isActive, isDisabled }) => {
      if (!date) {
        return null;
      }

      const dateKey = date.toString();
      const fare = faresByDate.get(dateKey);
      const monthMinimum = monthlyMinsByDate.get(dateKey.slice(0, 7));

      return (
        <BestFaresDateCell
          currencyCode={currencyCode}
          date={date}
          fare={fare}
          isActive={isActive}
          isDisabled={isDisabled}
          isLoading={isLoading}
          isLowestFare={fare ? monthMinimum === fare.amount : false}
          usePoints={usePoints}
        />
      );
    },
    [currencyCode, faresByDate, isLoading, monthlyMinsByDate, usePoints],
  );
};
